import { useState, useEffect, useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts'
import { Loader, X } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { resolveIanaTz, wallClockPartsInTz, parseWallClockInTzToMs } from '../lib/tz'
import { ChartHoverTooltip } from './dashboard/ChartHoverTooltip'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Renders ONE chart for a single selected parameter, with real data fetched
 * from /api/data/graph-data/. Title is "<Site Name> | <Parameter> (<unit>)".
 *
 * IMPORTANT: `data` and the `period` it was built for are stored together in a
 * single state object. The chart axis type/formatting is derived from that
 * stored period — never from the live `timePeriod` prop. This prevents a render
 * where the new axis type (e.g. numeric) sees old-format data (month-key
 * strings), which made recharts throw "DecimalError: Invalid argument: NaN".
 */
// Custom dot renderer for non-circle point shapes
function makeDot(shape, color) {
  if (shape === 'None') return false
  if (!shape || shape === 'Circle') return { r: 2, fill: color }
  return (props) => {
    const { cx, cy } = props
    if (cx == null || cy == null) return null
    const s = 4
    if (shape === 'Square')   return <rect x={cx - s} y={cy - s} width={s * 2} height={s * 2} fill={color} />
    if (shape === 'Diamond')  return <path d={`M${cx},${cy - s} L${cx + s},${cy} L${cx},${cy + s} L${cx - s},${cy} Z`} fill={color} />
    if (shape === 'Triangle') return <path d={`M${cx},${cy - s} L${cx + s},${cy + s} L${cx - s},${cy + s} Z`} fill={color} />
    return <circle cx={cx} cy={cy} r={2} fill={color} />
  }
}

export function SingleParameterChart({ param, dateRange = {}, timezone = 'UTC', timePeriod = 'Y', color = '#358ECD', pointShape = 'Circle', yMin, yMax, ySteps, viewMode = 'chart', onRemove, onData, embedded = false,
  // Charts sharing a syncId move their tooltip and crosshair together, so
  // hovering one stacked parameter reads all of them at the same instant.
  syncId,
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  // data + the period it represents, always consistent
  const [chart, setChart]     = useState({ data: [], period: timePeriod })
  const [meta, setMeta]       = useState({ unit: param.unit || '', latestValue: null })
  const [zoomDragStart, setZoomDragStart] = useState(null)
  const [zoomDragEnd,   setZoomDragEnd]   = useState(null)
  const [zoomDomain,    setZoomDomain]    = useState(null)

  const toNum = (v) => {
    if (v === null || v === undefined || v === '') return null
    const n = typeof v === 'string' ? parseFloat(v) : v
    return (typeof n === 'number' && !isNaN(n)) ? n : null
  }

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      if (!param?.id) return
      setLoading(true)
      setError(null)
      try {
        const response = await projectsApi.fetchGraphData(Number(param.id), {
          fromDate: dateRange.from,
          toDate:   dateRange.to,
          timezone,
        })
        if (cancelled) return

        if (!response || typeof response === 'string' || response.error === true || !Array.isArray(response.data)) {
          setChart({ data: [], period: timePeriod })
          setMeta({ unit: param.unit || '', latestValue: null })
          if (response?.error) setError(response.message || 'Failed to load data')
          return
        }

        // Build {x:number, y:number|null} points — both coerced, x always valid
        const points = []
        response.data.forEach((p) => {
          const xNum = typeof p.x === 'string' ? parseInt(p.x, 10) : p.x
          if (xNum === null || xNum === undefined || isNaN(xNum)) return
          points.push({ x: xNum, y: toNum(p.y) })
        })

        let processed
        if (timePeriod === 'Y') {
          // Group by month; X axis becomes category month-keys
          const grouped = {}
          points.forEach((pt) => {
            const d = new Date(pt.x)
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            grouped[key] = { x: key, y: pt.y, label: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}` }
          })
          if (dateRange.from && dateRange.to) {
            const out = []
            const from = new Date(dateRange.from)
            const to   = new Date(dateRange.to)
            let y = from.getFullYear(), m = from.getMonth() + 1
            const endY = to.getFullYear(), endM = to.getMonth() + 1
            let guard = 0
            while ((y < endY || (y === endY && m <= endM)) && guard < 240) {
              const key = `${y}-${String(m).padStart(2, '0')}`
              out.push(grouped[key] ? grouped[key] : { x: key, y: null, label: `${MONTH_NAMES[m - 1]} ${String(y).slice(-2)}` })
              m++; if (m > 12) { m = 1; y++ }
              guard++
            }
            processed = out
          } else {
            processed = Object.values(grouped).sort((a, b) => a.x.localeCompare(b.x))
          }
        } else {
          // Keep null points (hidden-status gaps from graph-data, plus
          // invalid/error nulls) so the line BREAKS at them with
          // connectNulls={false}, instead of dropping them — which would let the
          // trace reconnect straight across a hidden section. Just sort by time.
          processed = points.sort((a, b) => a.x - b.x)
        }

        // If there isn't a single numeric Y value, render as "no data"
        // (prevents recharts axis-domain NaN computation).
        const hasNumericY = processed.some((pt) => typeof pt.y === 'number' && !isNaN(pt.y))
        const finalData = hasNumericY ? processed : []
        setChart({ data: finalData, period: timePeriod })
        setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null)
        const u = response.unit || param.unit || ''
        setMeta({ unit: u, latestValue: response.latestValue ?? null })
        // Report data upward so the parent can build a combined CSV/export
        onData?.(param.id, { param, unit: u, period: timePeriod, points: finalData })
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load data')
          setChart({ data: [], period: timePeriod })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [param?.id, dateRange.from, dateRange.to, timezone, timePeriod])

  const unit = meta.unit || param.unit || ''
  const title = `${param.siteName || param.deviceName || 'Site'} | ${param.name}${unit ? ` (${unit})` : ''}`

  // Derive everything from the period the data was actually built for
  const renderPeriod = chart.period
  const data = chart.data
  const xIsCategory = renderPeriod === 'Y'

  const formatX = (value) => {
    if (value === null || value === undefined) return ''
    if (xIsCategory && typeof value === 'string' && value.includes('-')) {
      const [year, month] = value.split('-')
      return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year.slice(-2)}`
    }
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    switch (renderPeriod) {
      case 'M': return d.toLocaleString('en-US', { day: '2-digit' })
      case 'W': return d.toLocaleString('en-US', { weekday: 'short', day: '2-digit' })
      case 'D': return d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
      default:  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  // ── X-axis parity with the dashboard Line Chart Widget ──────────────────
  // The Parameter Comparison card previously let Recharts pick ticks from the
  // raw data extent (['dataMin','dataMax'] + preserveStartEnd), which produced
  // ragged labels anchored to the first sample (e.g. 04:10, 11:06, 18:03).
  // Instead, mirror the Line Chart: a fixed [start,end] day window with clean
  // even 2-hour ticks, all evaluated in the customer's timezone.
  const customerTz = useMemo(() => resolveIanaTz(timezone), [timezone])

  // Timezone- and period-aware tick labels, identical to the Line Chart Widget.
  const formatXAxisTick = (value) => {
    if (xIsCategory) return formatX(value)      // Y view: month-key strings
    const d = new Date(value)
    if (isNaN(d.getTime())) return ''
    // W view: 12-hour ticks — midnight shows the date ("Jul 30"), noon "12:00".
    if (renderPeriod === 'W') {
      const p = wallClockPartsInTz(d, customerTz)
      if (p.hour === 0 && p.minute === 0) {
        return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
      }
      return new Intl.DateTimeFormat('en-GB', { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
    }
    // M view: date labels ("Jul 8") at day boundaries, ~2-day spaced (mTicks).
    if (renderPeriod === 'M') {
      return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, month: 'short', day: 'numeric' }).format(d)
    }
    const opts = (() => {
      switch (renderPeriod) {
        case 'D':
        default:  return { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: false }
      }
    })()
    return new Intl.DateTimeFormat('en-GB', opts).format(d)
  }

  // D view bounds: explicit [start, end] in UTC ms from the picker range,
  // parsed as wall-clock in the customer TZ (falls back to a 24h window).
  const dWindowMs = useMemo(() => {
    if (renderPeriod !== 'D') return null
    const endMs   = parseWallClockInTzToMs(dateRange.to,   customerTz) ?? Date.now()
    const startMs = parseWallClockInTzToMs(dateRange.from, customerTz) ?? (endMs - 24 * 60 * 60 * 1000)
    return { startMs, endMs }
  }, [renderPeriod, dateRange.from, dateRange.to, customerTz])

  // D view: 2-hour tick marks anchored to even-hour boundaries in the
  // customer's TZ (00:00, 02:00, 04:00 …), exactly like the Line Chart.
  const dTicks = useMemo(() => {
    if (renderPeriod !== 'D' || !dWindowMs) return undefined
    const { startMs, endMs } = dWindowMs
    const startParts = wallClockPartsInTz(new Date(startMs), customerTz)
    const roundUpHour = startParts.minute === 0 && startParts.second === 0
      ? (startParts.hour % 2 === 0 ? startParts.hour : startParts.hour + 1)
      : startParts.hour + (startParts.hour % 2 === 0 ? 2 : 1)
    const firstTickStr =
      `${startParts.year}-${String(startParts.month).padStart(2, '0')}-` +
      `${String(startParts.day).padStart(2, '0')} ` +
      `${String(roundUpHour % 24).padStart(2, '0')}:00:00`
    const dayAdvance = Math.floor(roundUpHour / 24)
    let firstTickMs = parseWallClockInTzToMs(firstTickStr, customerTz)
    if (firstTickMs == null) return undefined
    firstTickMs += dayAdvance * 24 * 60 * 60 * 1000
    const ticks = []
    for (let t = firstTickMs; t <= endMs; t += 2 * 60 * 60 * 1000) ticks.push(t)
    return ticks
  }, [renderPeriod, dWindowMs, customerTz])

  // W view: 12-hour ticks at tz-local midnight & noon across the data window,
  // so the axis reads "Jul 30 · 12:00 · Jul 31 · 12:00 · …".
  const wTicks = useMemo(() => {
    if (renderPeriod !== 'W' || !data || data.length === 0) return undefined
    const xs = data.map((d) => d.x)
    const startMs = Math.min(...xs)
    const endMs   = Math.max(...xs)
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const onBoundary = sp.minute === 0 && sp.second === 0 && (sp.hour === 0 || sp.hour === 12)
    const nextHour = onBoundary ? sp.hour : (sp.hour < 12 ? 12 : 24)
    const dayAdvance = Math.floor(nextHour / 24)
    const firstStr =
      `${sp.year}-${String(sp.month).padStart(2, '0')}-${String(sp.day).padStart(2, '0')} ` +
      `${String(nextHour % 24).padStart(2, '0')}:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    firstMs += dayAdvance * 24 * 60 * 60 * 1000
    const t2 = []
    for (let t = firstMs; t <= endMs; t += 12 * 60 * 60 * 1000) t2.push(t)
    return t2
  }, [renderPeriod, data, customerTz])

  // M view: day-boundary ticks at a "nice" day-step (~15 labels) → "Jul 8 ·
  // Jul 10 · Jul 12 · …".
  const mTicks = useMemo(() => {
    if (renderPeriod !== 'M' || !data || data.length === 0) return undefined
    const xs = data.map((d) => d.x)
    const startMs = Math.min(...xs)
    const endMs   = Math.max(...xs)
    const totalDays = Math.max(1, Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)))
    const NICE = [1, 2, 3, 5, 7, 10, 14]
    const step = NICE.find((n) => n >= totalDays / 15) || 14
    const sp = wallClockPartsInTz(new Date(startMs), customerTz)
    const atMidnight = sp.hour === 0 && sp.minute === 0 && sp.second === 0
    const firstStr = `${sp.year}-${String(sp.month).padStart(2,'0')}-${String(sp.day).padStart(2,'0')} 00:00:00`
    let firstMs = parseWallClockInTzToMs(firstStr, customerTz)
    if (firstMs == null) return undefined
    if (!atMidnight) firstMs += 24 * 60 * 60 * 1000
    const out = []
    for (let t = firstMs; t <= endMs; t += step * 24 * 60 * 60 * 1000) out.push(t)
    return out
  }, [renderPeriod, data, customerTz])

  // Shared XD "data hover box" (see ChartHoverTooltip) so the System
  // Performance / comparison charts match every other chart's hover.
  const CustomTooltip = (p) => (
    <ChartHoverTooltip
      {...p}
      formatLabel={formatX}
      describe={(entry) => {
        const v = entry?.value
        const vv = typeof v === 'number' ? v.toFixed(2) : v
        return { name: title, valueText: `${vv}${unit ? ` ${unit}` : ''}`, color }
      }}
    />
  )

  return (
    <div className={embedded ? '' : 'bg-white rounded shadow-sm p-4'}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#4D4D4D] text-base">{title}</h3>
        {onRemove && (
          <button
            onClick={onRemove}
            title="Remove this parameter from comparison"
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="h-[200px] flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" /> Loading data…
        </div>
      ) : error ? (
        <div className="h-[200px] flex items-center justify-center text-red-500 text-sm">{error}</div>
      ) : data.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
          No data available for the selected time period
        </div>
      ) : viewMode === 'table' ? (
        <DataTable data={data} unit={unit} formatX={formatX} />
      ) : viewMode === 'stats' ? (
        <StatsView data={data} unit={unit} />
      ) : (
        <div className="relative">
          <ResponsiveContainer width="100%" height={200}>
            {/* key forces a clean remount when the axis type changes */}
            <LineChart
              key={renderPeriod}
              data={data}
              syncId={syncId}
              // Sync by x VALUE, not array index: each parameter can have a
              // different number of samples, so index-matching would line up
              // the wrong instants.
              syncMethod="value"
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
              onMouseDown={(e) => {
                if (e?.activeLabel == null) return
                setZoomDragStart(e.activeLabel); setZoomDragEnd(e.activeLabel)
              }}
              onMouseMove={(e) => {
                if (zoomDragStart == null || e?.activeLabel == null) return
                setZoomDragEnd(e.activeLabel)
              }}
              onMouseUp={() => {
                if (zoomDragStart == null || zoomDragEnd == null) { setZoomDragStart(null); setZoomDragEnd(null); return }
                if (zoomDragStart === zoomDragEnd) { setZoomDragStart(null); setZoomDragEnd(null); return }
                const lo = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                  ? Math.min(zoomDragStart, zoomDragEnd) : zoomDragStart
                const hi = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                  ? Math.max(zoomDragStart, zoomDragEnd) : zoomDragEnd
                setZoomDomain([lo, hi])
                setZoomDragStart(null); setZoomDragEnd(null)
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="x"
                type={xIsCategory ? 'category' : 'number'}
                {...(!xIsCategory ? {
                  scale: 'time',
                  domain: zoomDomain
                    ? zoomDomain
                    : (dWindowMs ? [dWindowMs.startMs, dWindowMs.endMs] : ['dataMin', 'dataMax']),
                  allowDataOverflow: !!zoomDomain,
                  // D → 2-hour ticks, W → 12-hour, M → ~2-day date ticks.
                  ...((dTicks || wTicks || mTicks) && !zoomDomain ? { ticks: dTicks || wTicks || mTicks } : {}),
                } : {
                  ...(zoomDomain ? { domain: zoomDomain, allowDataOverflow: true } : {}),
                })}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: 11, fill: '#888' }}
                interval={(renderPeriod === 'D' || renderPeriod === 'W' || renderPeriod === 'M') ? 0 : 'preserveStartEnd'}
                minTickGap={(renderPeriod === 'D' || renderPeriod === 'W' || renderPeriod === 'M') ? 0 : 50}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#888' }}
                width={40}
                allowDecimals
                allowDataOverflow={
                  (yMin !== undefined && yMin !== null && yMin !== '') ||
                  (yMax !== undefined && yMax !== null && yMax !== '')
                }
                domain={[
                  (yMin !== undefined && yMin !== null && yMin !== '') ? Number(yMin) : 'auto',
                  (yMax !== undefined && yMax !== null && yMax !== '') ? Number(yMax) : 'auto',
                ]}
                {...(() => {
                  const hasMin   = yMin   !== undefined && yMin   !== null && yMin   !== ''
                  const hasMax   = yMax   !== undefined && yMax   !== null && yMax   !== ''
                  const hasSteps = ySteps !== undefined && ySteps !== null && ySteps !== ''
                  if (hasMin && hasMax && hasSteps) {
                    const mn = Number(yMin), mx = Number(yMax)
                    const n  = Math.max(2, Math.round(Number(ySteps)))
                    const ticks = Array.from({ length: n }, (_, i) => {
                      const v = mn + (mx - mn) * i / (n - 1)
                      return Math.round(v * 100) / 100
                    })
                    return { ticks }
                  }
                  return hasSteps ? { tickCount: Number(ySteps) } : {}
                })()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                // Straight point-to-point, never a smoothed curve: a monotone
                // spline invents shape between samples, which reads as data
                // that was never measured.
                type="linear"
                dataKey="y"
                stroke={color}
                strokeWidth={2}
                dot={timePeriod === 'D' ? makeDot(pointShape, color) : false}
                isAnimationActive={false}
                // Break the line at null gaps — hidden-status readings come back
                // from graph-data as y:null, so the trace skips the hidden
                // section instead of connecting a segment straight across it.
                connectNulls={false}
              />
              {zoomDragStart != null && zoomDragEnd != null && zoomDragStart !== zoomDragEnd && (
                <ReferenceArea
                  x1={zoomDragStart}
                  x2={zoomDragEnd}
                  strokeOpacity={0.3}
                  fill="#358ECD"
                  fillOpacity={0.15}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
          {zoomDomain && (
            <button
              onClick={() => { setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null) }}
              className="absolute top-1 right-1 text-xs bg-white border border-gray-300 text-gray-600 px-2 py-0.5 rounded hover:bg-gray-50 shadow-sm"
            >
              Reset Zoom
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Data table view ──────────────────────────────────────────────────────────
function DataTable({ data, unit, formatX }) {
  const rows = data.filter(d => d.y !== null && d.y !== undefined)
  return (
    <div className="max-h-[240px] overflow-y-auto border border-gray-100 rounded">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-gray-600">Time</th>
            <th className="text-right px-3 py-2 font-semibold text-gray-600">Value{unit ? ` (${unit})` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="px-3 py-1.5 text-gray-700">{formatX(d.x)}</td>
              <td className="px-3 py-1.5 text-right text-gray-900">{typeof d.y === 'number' ? d.y.toFixed(2) : d.y}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Statistics view ──────────────────────────────────────────────────────────
function StatsView({ data, unit }) {
  const ys = data.map(d => d.y).filter(v => typeof v === 'number' && !isNaN(v))
  if (ys.length === 0) {
    return <div className="h-[120px] flex items-center justify-center text-gray-400 text-sm">No data to summarize</div>
  }
  const min = Math.min(...ys)
  const max = Math.max(...ys)
  const avg = ys.reduce((a, b) => a + b, 0) / ys.length
  const latest = ys[ys.length - 1]
  const fmt = (n) => (typeof n === 'number' ? n.toFixed(2) : n)
  const stats = [
    ['Minimum', fmt(min)],
    ['Maximum', fmt(max)],
    ['Average', fmt(avg)],
    ['Latest', fmt(latest)],
    ['Count', ys.length],
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 py-2">
      {stats.map(([label, val]) => (
        <div key={label} className="bg-gray-50 rounded p-3 text-center">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</div>
          <div className="text-base font-bold text-[#4D4D4D] mt-1">{val}{label !== 'Count' && unit ? ` ${unit}` : ''}</div>
        </div>
      ))}
    </div>
  )
}
